import type React from 'react';
import { useCallback, useMemo, useState } from 'react';

import { EventTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import { Checkbox, FormControlLabel, type SelectChangeEvent } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import { eventsAtom } from 'atoms/events';
import { levelExtensionAtom, tournamentIdAtom } from 'atoms/tournament';
import StandardButton from 'components/buttons/StandardButton';
import { useStyles } from 'components/dialogs/addToEventDialog/styles';
import { useStyles as dialogStyles } from 'components/dialogs/styles';
import TMXDialog from 'components/dialogs/TMXDialog';
import FormErrorMessage from 'components/forms/FormErrorMessage';
import TMXSelect from 'components/selectors/tmxSelector/TMXSelect';
import { BodyRegular, FormLabelTextLarge, H3 } from 'components/typography/Typography';
import { usePersonContext } from 'hooks/person/usePersonContext';
import useMutateTournament from 'hooks/tournament/useMutateTournament';
import { EligibilityStatus, type PersonEventEligibility } from 'typedefs/event';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';

import EligibilityContainer from './EligibilityContainer';
import { getParticipantDisplayName, getSortedParticipantsEligibility } from './utils';

const { ELIGIBLE } = EligibilityStatus;

interface AddToEventDialogProps {
  open?: boolean;
  isTeam?: boolean;
  participants: ParticipantInterface[];
  onClose: () => void;
  onExited?: () => void;
  onResetRowSelection?: () => void;
}

const AddToEventDialog: React.FC<React.PropsWithChildren<AddToEventDialogProps>> = ({
  open,
  isTeam = false,
  participants,
  onClose,
  onExited,
  onResetRowSelection,
}) => {
  const { t } = useTranslation();
  const tournamentId = useAtomValue(tournamentIdAtom);
  const { getPersonEventEligibility } = usePersonContext();
  const events = useAtomValue(eventsAtom);
  const { level } = useAtomValue(levelExtensionAtom);
  const { classes } = useStyles();
  const { classes: dialogClasses } = dialogStyles();

  const [addToEvent, isSaving] = useMutateTournament('addToEvent');

  const [save, setSave] = useState<boolean>(false);
  const [addPartEligibleParticipants, setAddPartEligibleParticipants] = useState<boolean>(false);
  const [participantsEventEligibility, setParticipantsEventEligibility] = useState<PersonEventEligibility[] | []>([]);
  const [selectedOption, setSelectedOption] = useState({
    id: '',
    drawId: '',
    name: '',
    type: '',
  });
  const [{ eligibilityLoading }, setGetParticipantsEligibilityResult] = useState<{ eligibilityLoading?: boolean }>({
    eligibilityLoading: false,
  });

  const handleOnChangeOption = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newOption = events.find((currentEvent) => currentEvent.eventId === event.target.value);
      const drawDefinitions = newOption?.drawDefinitions;
      const drawId = drawDefinitions?.[0]?.drawId;
      setSelectedOption({
        id: newOption.eventId,
        drawId,
        name: newOption.eventName,
        type: newOption.eventType,
      });
      setSave(false);
      setParticipantsEventEligibility([]);
    },
    [events],
  );

  const handleOnChangeAddPartEligibleParticipants = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setAddPartEligibleParticipants(event.target.checked),
    [],
  );

  const isUnsanctionedTournament = level?.name === 'Unsanctioned';
  const { eligibleParticipants, partEligibleParticipants, ineligibleParticipants } = useMemo(
    () => getSortedParticipantsEligibility(participantsEventEligibility, isUnsanctionedTournament),
    [participantsEventEligibility, isUnsanctionedTournament],
  );

  const eligible =
    !!eligibleParticipants?.length || (addPartEligibleParticipants && !!partEligibleParticipants?.length);

  const isOnlyIneligible = useMemo(() => {
    return !!ineligibleParticipants.length && !eligibleParticipants?.length && !partEligibleParticipants?.length;
  }, [ineligibleParticipants, eligibleParticipants, partEligibleParticipants]);

  const validEventsToAssign = useMemo(
    () => events.filter((event) => (isTeam ? event?.eventType === EventTypeEnum.TEAM : true)) || [],
    [events, isTeam],
  );

  const isAddToEventDiabled =
    eligibilityLoading || !selectedOption?.id || (!eligible && !!participantsEventEligibility?.length);

  const isEligibilePlayersToAdd = !![
    ...eligibleParticipants,
    ...(addPartEligibleParticipants ? partEligibleParticipants : []),
  ].length;

  const handleSave = useCallback(async () => {
    if (!save) {
      const getParticipantsEligibility = async (): Promise<PersonEventEligibility[]> => {
        if (isTeam || isUnsanctionedTournament) {
          return participants.map((p) => ({
            participantId: p?.participantId,
            participantName: getParticipantDisplayName(p, isTeam),
            eligibilityStatus: ELIGIBLE,
          }));
        } else {
          setGetParticipantsEligibilityResult({ eligibilityLoading: true });

          const participantsEligibilityData = await getPersonEventEligibility?.(
            participants?.map((p) => p?.participantId),
            tournamentId,
            selectedOption.id,
          );
          const mappedEligibilityData = participantsEligibilityData?.data?.personEventEligibility?.map(
            (elgibilityData, index) => {
              const participant = participants[index];
              return {
                participantId: participant?.participantId,
                participantName: getParticipantDisplayName(participant, isTeam),
                ...elgibilityData,
              };
            },
          );

          setGetParticipantsEligibilityResult({ eligibilityLoading: false });

          return mappedEligibilityData;
        }
      };

      const participantsEligibility = await getParticipantsEligibility();

      if (participantsEligibility?.length) {
        setSave(true);
        setParticipantsEventEligibility([]);
        setParticipantsEventEligibility(participantsEligibility);
      }
    } else {
      const eligibleParticipantIds = [
        ...eligibleParticipants,
        ...(addPartEligibleParticipants ? partEligibleParticipants : []),
      ]?.map((p) => p?.participantId);

      await addToEvent(
        selectedOption.id,
        eligibleParticipantIds,
        selectedOption.type === EventTypeEnum.SINGLES,
        selectedOption.drawId,
        isTeam,
      );
      onResetRowSelection?.();
      onClose();
      setSave(false);
    }
  }, [
    addPartEligibleParticipants,
    addToEvent,
    eligibleParticipants,
    getPersonEventEligibility,
    isTeam,
    isUnsanctionedTournament,
    onClose,
    onResetRowSelection,
    partEligibleParticipants,
    participants,
    save,
    selectedOption.drawId,
    selectedOption.id,
    selectedOption.type,
    tournamentId,
  ]);

  const paperProps = useMemo(() => ({ className: dialogClasses.paper }), [dialogClasses.paper]);

  return (
    <TMXDialog
      fullWidth
      id="add-to-event-dialog"
      open={open || isSaving}
      PaperProps={paperProps}
      TransitionProps={{
        onEntering: () => {
          setSave(false);
          setParticipantsEventEligibility([]);
        },
        onExited,
      }}
      onClose={onClose}
    >
      <DialogContent sx={{ padding: 0 }}>
        <Grid container direction="column" gap={2}>
          <Grid item>
            <H3>{t('addToEvent')}</H3>
          </Grid>
          <Grid item>
            <BodyRegular>
              {t(isTeam ? 'numberTeamsSelected' : 'numberPlayersSelected', { number: participants?.length })}
            </BodyRegular>
          </Grid>
          <Grid item>
            <FormLabelTextLarge>{t('addToEventDialog.chooseAnEvent')}</FormLabelTextLarge>
            <TMXSelect
              displayEmpty
              fullWidth
              disabled={eligibilityLoading}
              id="add-to-event-dialog-select"
              value={selectedOption.id}
              onChange={handleOnChangeOption}
            >
              <MenuItem disabled selected value="">
                {t('matchTypePh')}
              </MenuItem>
              {validEventsToAssign.map((event) => (
                <MenuItem key={event.eventId} value={event.eventId}>
                  {event.eventName}
                </MenuItem>
              ))}
            </TMXSelect>
          </Grid>
          {eligibilityLoading ? (
            <CircularProgress className={classes.loading} />
          ) : (
            <Grid container item direction="column">
              <EligibilityContainer isTeam={isTeam} participantsEventEligibility={participantsEventEligibility} />
              {!!partEligibleParticipants?.length && (
                <Grid container item alignItems="center" direction="row">
                  <Grid item>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={addPartEligibleParticipants}
                          color={'primary'}
                          onChange={handleOnChangeAddPartEligibleParticipants}
                        />
                      }
                      label={t('addToEventDialog.includePartEligiblePlayers')}
                    />
                  </Grid>
                </Grid>
              )}
              {!isEligibilePlayersToAdd && !isOnlyIneligible && save && (
                <FormErrorMessage message={t('addToEventDialog.noEligiblePlayers')} />
              )}
              {isOnlyIneligible && <FormErrorMessage message={t('addToEventDialog.onlyIneligiblePlayers')} />}
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actionsContainer}>
        <StandardButton id="add-to-event-dialog-cancel-btn" onClick={onClose}>
          {t('actions.cancel')}
        </StandardButton>
        <StandardButton
          disabled={isAddToEventDiabled}
          id="add-to-event-dialog-save-btn"
          loading={isSaving}
          variant="primary"
          onClick={handleSave}
        >
          {t('addToEvent')}
        </StandardButton>
      </DialogActions>
    </TMXDialog>
  );
};

export default AddToEventDialog;
